import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router'

import { AWXConnectedAccountStatus, type AWXEntity } from '@ankor-io/common/types/awx'

import { useConnectedAccount } from '@/providers/connectedAccount'
import { type ConnectedAccount } from '@/providers/connectedAccount/types'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogTitle } from '@/ui-components/Dialog'

import { Step1SelectEntityName } from './steps/Step1SelectEntityName'
import { Step2CreateConnectedAccount } from './steps/Step2CreateConnectedAccount'

type NewConnectedAccountModalProps = {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  emitConnectedAccountCreated?: () => void
}

export const NewConnectedAccountModal = ({
  isOpen,
  setIsOpen,
  emitConnectedAccountCreated,
}: NewConnectedAccountModalProps) => {
  const navigate = useNavigate()
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, updateActiveConnectedAccountId } = useUserContext()
  const { appendConnectedAccount } = useConnectedAccount()

  const [step, setStep] = useState(1)
  const [entryType, setEntryType] = useState<'existing' | 'new'>('existing')
  const [selectedEntity, setSelectedEntity] = useState<AWXEntity | null>(null)
  const [newEntityName, setNewEntityName] = useState('')
  const [connectedAccountName, setConnectedAccountName] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const resetState = () => {
    setStep(1)
    setEntryType('existing')
    setSelectedEntity(null)
    setNewEntityName('')
    setConnectedAccountName('')
  }

  const handleOpenChange = (value: boolean) => {
    setIsOpen(value)
    if (!value) {
      resetState()
    }
  }

  const handleNext = () => {
    setStep(step + 1)
  }

  const handleBack = () => {
    setStep(step - 1)
  }

  const handleCreate = () => {
    const createConnectedAccount = async () => {
      if (!activeOrganizationId) {
        throw new Error('Missing active organization')
      }

      setIsLoading(true)

      const res = await fetch(`/api/pay/org/${activeOrganizationId}/connected-account`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAccessToken()}`,
        },
        body: JSON.stringify(
          entryType === 'existing'
            ? {
                name: connectedAccountName,
                awxEntityId: selectedEntity?.id,
              }
            : {
                name: connectedAccountName,
              },
        ),
      })

      if (!res.ok) {
        console.error(res)
        setIsLoading(false)
        throw new Error('Failed to create connected account')
      }

      const response = await res.json()

      if (!response.connectedAccountId) {
        throw new Error('Failed to create connected account')
      }

      const newConnectedAccount: ConnectedAccount = {
        id: response.connectedAccountId,
        name: connectedAccountName,
        organization_id: activeOrganizationId,
        status: AWXConnectedAccountStatus.CREATED,
      }

      appendConnectedAccount(newConnectedAccount)
      updateActiveConnectedAccountId(newConnectedAccount.id)
      setIsLoading(false)
      emitConnectedAccountCreated?.()
      setIsOpen(false)
      navigate(`/`)
    }

    toast.promise(
      createConnectedAccount(),
      {
        loading: 'Creating account...',
        success: 'Successfully created account!',
        error: 'Failed to create account',
      },
      { position: 'top-right' },
    )
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleOpenChange}
      data-testid="new-connected-account-modal"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <DialogTitle>Create New Vessel</DialogTitle>
        </div>
        <Button
          plain
          data-testid="close-modal-button"
          onClick={() => setIsOpen(false)}
        >
          <X data-slot="icon" />
        </Button>
      </div>
      {step === 1 && (
        <Step1SelectEntityName
          setIsOpen={setIsOpen}
          onNext={handleNext}
          entryType={entryType}
          setEntryType={setEntryType}
          selectedEntity={selectedEntity}
          setSelectedEntity={setSelectedEntity}
          newEntityName={newEntityName}
          setNewEntityName={setNewEntityName}
        />
      )}
      {step === 2 && (
        <Step2CreateConnectedAccount
          onBack={handleBack}
          entryType={entryType}
          selectedEntity={selectedEntity}
          newEntityName={newEntityName}
          setNewEntityName={setNewEntityName}
          isLoading={isLoading}
          connectedAccountName={connectedAccountName}
          setConnectedAccountName={setConnectedAccountName}
          onCreate={handleCreate}
        />
      )}
    </Dialog>
  )
}

export default NewConnectedAccountModal
